import { useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import type { Member } from './MemberPicker.js';

interface MemberAvatarProps {
  member: Member;
  size: 'xs' | 'sm' | 'md';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business MemberPicker のアバター画像。avatarUrl があれば img を出し、読み込み失敗時は initials span にフォールバックする
 * @graph-connects none
 */
function MemberAvatar({ member, size }: MemberAvatarProps): ReactNode {
  const className = `cortex-mp-avatar cortex-mp-avatar--${size}`;
  const [imageFailed, setImageFailed] = useState<boolean>(false);
  const hasUrl = member.avatarUrl !== undefined && member.avatarUrl !== '';
  if (hasUrl && !imageFailed) {
    return (
      <img
        className={className}
        src={member.avatarUrl}
        alt=""
        data-testid="cortex-mp-avatar"
        onError={() => {
          setImageFailed(true);
        }}
      />
    );
  }
  return (
    <span
      className={`${className} cortex-mp-avatar--fallback`}
      aria-hidden="true"
      data-testid="cortex-mp-avatar"
    >
      {getInitials(member.name)}
    </span>
  );
}

/** @graph-connects none */
function getInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') return '?';
  const parts = trimmed.split(/\s+/u);
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2);
  return `${(parts[0] ?? '').charAt(0)}${(parts[parts.length - 1] ?? '').charAt(0)}`;
}

interface TriggerContentArgs {
  multiple: boolean;
  selectedIds: readonly string[];
  memberById: Map<string, Member>;
  placeholder: string;
  disabled: boolean;
  onRemove?: (event: ReactMouseEvent<HTMLButtonElement>, memberId: string) => void;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business MemberPicker trigger の選択済み表示。単一は avatar + 名前 + チーム、複数は chip 列
 * @graph-connects none
 */
export function renderTriggerContent(args: TriggerContentArgs): ReactNode {
  const { multiple, selectedIds, memberById, placeholder, disabled, onRemove } = args;
  if (selectedIds.length === 0) {
    return <span className="cortex-mp-placeholder">{placeholder}</span>;
  }
  if (!multiple) {
    const id = selectedIds[0];
    const member = id === undefined ? undefined : memberById.get(id);
    if (member === undefined) {
      return <span className="cortex-mp-single-value">{id ?? placeholder}</span>;
    }
    const team = member.department;
    return (
      <span className="cortex-mp-single-value">
        <MemberAvatar key={member.id} member={member} size="md" />
        <span className="cortex-mp-single-value-text">
          <span className="cortex-mp-single-value-name">{member.name}</span>
          {team !== undefined && team !== '' && (
            <span className="cortex-mp-single-value-team">{team}</span>
          )}
        </span>
      </span>
    );
  }
  return (
    <>
      {selectedIds.map((id) => {
        const member = memberById.get(id);
        const label = member?.name ?? id;
        return (
          <span key={id} className="cortex-mp-chip" title={label}>
            {member !== undefined && <MemberAvatar member={member} size="xs" />}
            <span className="cortex-mp-chip-name">{label}</span>
            {!disabled && onRemove !== undefined && (
              <button
                type="button"
                className="cortex-mp-chip-remove"
                aria-label={`${label} を選択解除`}
                onClick={(e) => {
                  onRemove(e, id);
                }}
              >
                ×
              </button>
            )}
          </span>
        );
      })}
    </>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business MemberPicker の listbox 行のデフォルトレンダラ。avatar + 名前 + team badge + description + 選択時 check。
 * チーム (department) は名前の右に badge として表示する。検索プレースホルダで「チームで検索」と謳う以上、
 * 何で絞れるか視覚的に把握できるようにする必要がある
 * @graph-connects none
 */
export function defaultRenderOption(member: Member, selected: boolean): ReactNode {
  const team = member.department;
  const description = member.description ?? member.email;
  const hasTeam = team !== undefined && team !== '';
  const hasDescription = description !== undefined && description !== '';
  const descriptionClassName =
    member.description === undefined
      ? 'cortex-mp-option-description cortex-mp-option-email'
      : 'cortex-mp-option-description';
  return (
    <>
      <MemberAvatar member={member} size="md" />
      <span className="cortex-mp-option-main">
        <span className="cortex-mp-option-name-row">
          <span className="cortex-mp-option-name">{member.name}</span>
          {hasTeam && (
            <span className="cortex-mp-team-badge" title={team}>
              {team}
            </span>
          )}
        </span>
        {hasDescription && <span className={descriptionClassName}>{description}</span>}
      </span>
      {selected && (
        <svg
          className="cortex-mp-option-check"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </>
  );
}
